import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Printer } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody, DialogFooter,
} from '@/components/ui/Dialog';
import Button from '@/components/ui/Button';
import { DataSource } from '../types';

const READ_KEYS = [
  'extInt.exchange.read.identity',
  'extInt.exchange.read.structure',
  'extInt.exchange.read.role',
  'extInt.exchange.read.employment',
  'extInt.exchange.read.goals',
  'extInt.exchange.read.skills',
  'extInt.exchange.read.equipment',
];

const NEVER_KEYS = [
  'extInt.exchange.never.pay',
  'extInt.exchange.never.bank',
  'extInt.exchange.never.medical',
  'extInt.exchange.never.identifiers',
  'extInt.exchange.never.cases',
  'extInt.exchange.never.documents',
];

interface DataExchangeSummaryProps {
  source: DataSource | undefined;
  actorName: string;
  onSign: (sourceId: string) => void;
  onClose: () => void;
}

const DataExchangeSummary: React.FC<DataExchangeSummaryProps> = ({
  source, actorName, onSign, onClose,
}) => {
  const { t } = useTranslation();
  const [acknowledged, setAcknowledged] = useState(false);
  if (!source) return null;

  // Captured so the sign handler keeps the narrowed type inside its closure.
  const activeSource = source;
  const signed = Boolean(source.exchangeSignedOn);

  return (
    <Dialog open onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="max-w-3xl" data-testid="exchange-summary">
        <DialogHeader>
          <DialogTitle>{t('extInt.exchange.title')}</DialogTitle>
          <DialogDescription>{t('extInt.exchange.description')}</DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <section className="rounded-md border border-border p-4">
              <h3 className="text-sm font-semibold text-foreground">{t('extInt.exchange.readHeading')}</h3>
              <ul className="mt-2 space-y-1.5 text-sm text-muted-foreground">
                {READ_KEYS.map((key) => <li key={key}>{t(key)}</li>)}
              </ul>
            </section>
            <section className="rounded-md border border-border p-4">
              <h3 className="text-sm font-semibold text-foreground">{t('extInt.exchange.neverHeading')}</h3>
              <ul className="mt-2 space-y-1.5 text-sm text-muted-foreground">
                {NEVER_KEYS.map((key) => <li key={key}>{t(key)}</li>)}
              </ul>
            </section>
          </div>

          <section className="rounded-md border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
            <p>{t('extInt.exchange.direction')}</p>
            <p className="mt-2">{t('extInt.exchange.retention')}</p>
          </section>

          {signed ? (
            <p className="text-sm text-foreground" data-testid="exchange-signed">
              {t('extInt.exchange.signedBy', {
                name: source.exchangeSignedByName,
                date: source.exchangeSignedOn
                  ? new Date(source.exchangeSignedOn).toLocaleDateString()
                  : '',
              })}
            </p>
          ) : (
            <label className="flex items-start gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                className="mt-1"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                data-testid="exchange-ack"
              />
              <span>{t('extInt.exchange.acknowledge', { name: actorName })}</span>
            </label>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => window.print()} data-testid="exchange-print">
            <Printer className="me-2 h-4 w-4" aria-hidden="true" />
            {t('extInt.exchange.print')}
          </Button>
          {!signed && (
            <Button onClick={() => onSign(activeSource.id)} disabled={!acknowledged} data-testid="exchange-sign">
              {t('extInt.exchange.sign')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DataExchangeSummary;
